import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Lightbulb, X, CheckCircle2 } from "lucide-react";
import { CURRENCIES } from "@/lib/CurrencyContext";

/**
 * OnboardingBanner — persistent "what's the next step?" coachmark for the
 * Project Dashboard. Auto-detects what's missing from the project and shows
 * a single, dismissible CTA. Dismissal + completion are stored per-project
 * in localStorage so they survive reloads.
 */
const STORAGE_KEY = (pid) => `flipcost.onboarding.${pid}`;
const GLOBAL_KEY = "flipcost.onboarding.global";

function readState(pid) {
  if (!pid) return {};
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY(pid)) || "{}");
  } catch (_e) {
    return {};
  }
}

function writeState(pid, patch) {
  if (!pid) return;
  const next = { ...readState(pid), ...patch };
  try { localStorage.setItem(STORAGE_KEY(pid), JSON.stringify(next)); } catch (_e) {}
}

function readGlobal() {
  try { return JSON.parse(localStorage.getItem(GLOBAL_KEY) || "{}"); }
  catch (_e) { return {}; }
}

function writeGlobal(patch) {
  const next = { ...readGlobal(), ...patch };
  try { localStorage.setItem(GLOBAL_KEY, JSON.stringify(next)); } catch (_e) {}
}

function detectStep(project, state, globalState) {
  // 1. Confirm currency.
  if (!state.currencyAck && (!project.currency || project.currency === "USD")) {
    return "currency";
  }
  // 2. Overview — purchase price OR floor area still missing.
  if (!project.floor_area_sqm || project.floor_area_sqm <= 0
      || !project.purchase_price || project.purchase_price <= 0) {
    return "overview";
  }
  // 3. Cost Library review — user-wide, not per-project. Default rates are
  //    generic assumptions; users MUST validate them against local quotes
  //    before they trust any number downstream.
  if (!globalState.libraryReviewed) {
    return "library";
  }
  // 4. No rooms yet.
  if (!project.rooms || project.rooms.length === 0) {
    return "rooms";
  }
  // 5. Final nudge — point them at the rest of the tabs (Soft Costs,
  //    Extensions, Actuals mode, etc.) before dismissing the banner.
  if (!state.navAck) {
    return "navigate";
  }
  return "done";
}

const TOTAL_STEPS = 5;

const STEP_META = {
  currency: {
    progress: 1,
    headline: "Welcome to FlipCost — let's set this up.",
    body: "First, pick your project currency below. Default cost rates auto-scale to your region — change this if you're not in the United States.",
    cta: "I'm done",
  },
  overview: {
    progress: 2,
    headline: "Step 2 of 5 — Tell us about the property.",
    body: "Open the Overview tab and scroll to the Key Inputs section to set your purchase price, target sale price and floor area. These power your profit, ROI, and cost-per-sqm.",
    cta: "Open Overview & scroll to Key Inputs",
  },
  library: {
    progress: 3,
    headline: "Step 3 of 5 — Verify your cost assumptions.",
    body: "Default rates are generic regional estimates. Review the Cost Library and adjust any rate that doesn't match your local contractor quotes — this is the single most important step for accurate numbers. Do this before you add rooms.",
    cta: "Review Cost Library",
  },
  rooms: {
    progress: 4,
    headline: "Step 4 of 5 — Add your first room.",
    body: "Start with the kitchen or main bathroom — they have the highest impact on cost and value. We'll generate default work items you can edit.",
    cta: "Add a room",
  },
  navigate: {
    progress: 5,
    headline: "Step 5 of 5 — Navigate the tabs above.",
    body: "You're set up. Use the tabs above to fine-tune Soft Costs (transfer, holding, agent fees), add Extensions, edit your Cost Library, and switch to Actual mode when you start tracking real spend.",
    cta: "Close",
  },
  done: {
    progress: TOTAL_STEPS,
    headline: "You're all set up.",
    body: "Switch to Actual mode any time to start tracking what you've actually spent against your plan.",
    cta: "Got it",
    done: true,
  },
};

export default function OnboardingBanner({
  project,
  onSetTab,
  onAcknowledgeCurrency,
  onChangeCurrency,
  onReviewLibrary,
}) {
  const pid = project?.id;
  const [persisted, setPersisted] = useState(() => readState(pid));
  const [globalState, setGlobalState] = useState(() => readGlobal());

  // Re-read when the project switches.
  useEffect(() => { setPersisted(readState(pid)); }, [pid]);

  const step = useMemo(
    () => detectStep(project || {}, persisted, globalState),
    [project, persisted, globalState]
  );

  // Persist completion so the green "all set" card doesn't keep popping back.
  useEffect(() => {
    if (step === "done" && pid && !persisted.completed) {
      writeState(pid, { completed: true });
      setPersisted((p) => ({ ...p, completed: true }));
    }
  }, [step, pid, persisted.completed]);

  if (!pid) return null;
  if (persisted.dismissed) return null;
  if (step === "done" && persisted.completed) return null;

  const meta = STEP_META[step];

  const handleCta = () => {
    if (step === "currency") {
      onAcknowledgeCurrency?.();
      writeState(pid, { currencyAck: true });
      setPersisted((p) => ({ ...p, currencyAck: true }));
      return;
    }
    if (step === "overview") {
      onSetTab?.("overview");
      // Smooth-scroll to Key Inputs after the tab swap renders. We can't use
      // scrollIntoView({block:"start"}) directly because the sticky header
      // (and sometimes an impersonation/onboarding banner above it) covers
      // ~100px at the top of the viewport, hiding the "Key Inputs" heading
      // and making it look like we landed on Construction Details instead.
      // Measure the section's absolute Y and subtract a generous offset so
      // the heading lands ~16px below the sticky header.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="key-inputs-card"]');
        if (el) {
          // Header is a `.fc-header-bar` sticky element. Measure its real
          // height at scroll time so future header changes don't break this.
          const sticky = document.querySelector(".fc-header-bar");
          const headerH = sticky ? sticky.getBoundingClientRect().height : 72;
          const targetTop =
            el.getBoundingClientRect().top + window.scrollY - headerH - 16;
          window.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
          el.classList.add("ring-2", "ring-sage-700", "ring-offset-2");
          setTimeout(() => el.classList.remove("ring-2", "ring-sage-700", "ring-offset-2"), 2000);
        }
      }, 80);
      return;
    }
    if (step === "library") {
      // Mark reviewed immediately — clicking the CTA is enough of an
      // acknowledgement; user can return whenever they like to update rates.
      writeGlobal({ libraryReviewed: true });
      setGlobalState((s) => ({ ...s, libraryReviewed: true }));
      onReviewLibrary?.();
      return;
    }
    if (step === "rooms") onSetTab?.("rooms");
    if (step === "navigate") {
      // Final step — user has fully seen the tabs callout. Mark
      // acknowledged AND dismiss in one click so the banner disappears
      // and doesn't pop back if they create a new project later.
      writeState(pid, { navAck: true, dismissed: true });
      setPersisted((p) => ({ ...p, navAck: true, dismissed: true }));
    }
    if (step === "done") {
      writeState(pid, { dismissed: true });
      setPersisted((p) => ({ ...p, dismissed: true }));
    }
  };

  const handleInlineCurrency = (next) => {
    onChangeCurrency?.(next);
    // Auto-acknowledge so the banner advances as soon as a currency is picked.
    writeState(pid, { currencyAck: true });
    setPersisted((p) => ({ ...p, currencyAck: true }));
  };

  const handleDismiss = () => {
    writeState(pid, { dismissed: true });
    setPersisted((p) => ({ ...p, dismissed: true }));
  };

  const Icon = meta.done ? CheckCircle2 : Lightbulb;

  return (
    <div
      className="max-w-7xl mx-auto px-6 pt-5"
      data-testid="onboarding-banner"
      data-onboarding-step={step}
    >
      <div className="rounded-xl border border-sage-200 bg-gradient-to-br from-sage-50 to-white p-5 shadow-sm fc-fade-in">
        <div className="flex items-start gap-4">
          <div
            className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${
              meta.done
                ? "bg-[#059669] text-white"
                : "bg-sage-100 border border-sage-200 text-sage-700"
            }`}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className="fc-heading text-[#1C1C1C] text-[16px] font-medium"
              data-testid="onboarding-headline"
            >
              {meta.headline}
            </h3>
            <p className="text-sm text-[#57534E] mt-1.5 leading-relaxed">{meta.body}</p>
            <div className="flex items-center gap-3 mt-3.5 flex-wrap">
              {step === "currency" && (
                <select
                  data-testid="onboarding-currency-select"
                  value={project?.currency || "USD"}
                  onChange={(e) => handleInlineCurrency(e.target.value)}
                  className="fc-input h-9 px-2.5 text-sm rounded-md bg-white"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.symbol} {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={handleCta}
                data-testid="onboarding-cta-btn"
                className={`text-white rounded-lg h-9 px-3.5 text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                  meta.done
                    ? "bg-[#059669] hover:bg-[#047857]"
                    : "bg-[#1C1C1C] hover:bg-[#2A2A2E]"
                }`}
              >
                {meta.cta} <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <ProgressDots progress={meta.progress} done={meta.done} total={TOTAL_STEPS} />
              <span className="text-[11px] text-[#A8A29E]">
                {meta.done ? "Setup complete" : `${meta.progress} of ${TOTAL_STEPS}`}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="onboarding-dismiss-btn"
            className="text-[#A8A29E] hover:text-[#1C1C1C] transition-colors shrink-0"
            aria-label="Dismiss onboarding"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ProgressDots({ progress, done, total = 5 }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => {
        const filled = done || i < progress;
        return (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full ${
              done ? "bg-[#059669]" : filled ? "bg-sage-700" : "bg-black/10"
            }`}
          />
        );
      })}
    </div>
  );
}

/**
 * Convenience: clear the persisted onboarding state for a project so the
 * banner re-appears at step 1. Wired to the Restart-tour menu item later.
 */
export function resetOnboarding(pid) {
  if (!pid) return;
  try { localStorage.removeItem(STORAGE_KEY(pid)); } catch (_e) {}
}
